import { gql, useSubscription } from "@apollo/client";
import clsx from "clsx";
import { Tooltip } from "swash/Tooltip";

import { ExposureIcon } from "@/containers/ExposureIcon";
import { PrintCoreExposure } from "@/containers/article/PrintCoreExposure";

import {
  DelayedVsArticlePublicationIndicator,
  DisplayPriorArticlePublicationIndicator,
  ExposureIndicatorTooltip,
  ShouldDisplayDelayedVsArticlePublicationIndicator,
  ShouldDisplayIndicators,
  ShouldDisplayPriorArticlePublicationIndicator,
} from "./ExposureIndicatorTooltip";

const IndicatorContainer = ({ children }) => {
  return <div className="absolute -bottom-[15%] left-1/2">{children}</div>;
};

const TooltipContent = (props) => {
  useSubscription(ArticleExposurePlannedDatetimeSubscription, {
    variables: {
      where: {
        exposureId: { eq: props.exposure.id },
      },
    },
  });

  return <ExposureIndicatorTooltip {...props} />;
};

const Exposure = ({ exposure, articleExposure }) => {
  return (
    <Tooltip
      tooltip={
        <TooltipContent articleExposure={articleExposure} exposure={exposure} />
      }
    >
      <div className="relative flex">
        <ExposureIcon
          exposure={exposure}
          className={clsx(!articleExposure.fulfilled && "opacity-disabled")}
        />
        <ShouldDisplayIndicators articleExposure={articleExposure}>
          <ShouldDisplayPriorArticlePublicationIndicator
            articleExposure={articleExposure}
            exposure={exposure}
          >
            <IndicatorContainer>
              <DisplayPriorArticlePublicationIndicator className="text-[0.8rem]" />
            </IndicatorContainer>
          </ShouldDisplayPriorArticlePublicationIndicator>
          <ShouldDisplayDelayedVsArticlePublicationIndicator
            articleExposure={articleExposure}
            exposure={exposure}
          >
            <IndicatorContainer>
              <DelayedVsArticlePublicationIndicator className="text-[0.8rem]" />
            </IndicatorContainer>
          </ShouldDisplayDelayedVsArticlePublicationIndicator>
        </ShouldDisplayIndicators>
      </div>
    </Tooltip>
  );
};

export const ExposureIndicator = ({ articleExposure, exposure, article }) => {
  if (!articleExposure.suggested && !articleExposure.fulfilled) return null;
  if (exposure.type === "print") {
    return <PrintCoreExposure article={article} />;
  }

  return (
    <Exposure
      articleExposure={{ ...articleExposure, article }}
      exposure={exposure}
    />
  );
};

ExposureIndicator.fragments = {
  articleExposure: gql`
    fragment ExposureIndicator_articleExposure on ArticleExposure {
      gid
      exposureId
      suggested
      fulfilled
      fulfilledAt
      publicationDate
      planning {
        range
        date
      }
    }
  `,
  exposure: gql`
    fragment ExposureIndicator_exposure on Exposure {
      id
      label
      ...ExposureIcon_exposure
    }

    ${ExposureIcon.fragments.exposure}
  `,
  publication: gql`
    fragment ExposureIndicator_publication on Publication {
      id
      date
    }
  `,
  article: gql`
    fragment ExposureIndicator_article on Article {
      id
      ...PrintCoreExposure_article
    }

    ${PrintCoreExposure.fragments.article}
  `,
};

const ArticleExposurePlannedDatetimeSubscription = gql`
  subscription ExposureIndicator_ArticleExposureUpdated(
    $where: ExposureArticleExposureWhere!
  ) {
    articleExposureUpdated(where: $where) {
      gid
      ...ExposureIndicator_articleExposure
    }
  }
  ${ExposureIndicator.fragments.articleExposure}
`;
